'use client';
import React from 'react';
import Image from 'next/image';
import Title from '@/components/Title/';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/all';
import { Lottie } from '@/components/LottieWrapper';
import Button from '@/components/Button';
import { useLottieLoader } from '@/lib/useLottieLoader';
import { SM_BREAKPOINT } from '@/lib/constants';
import clsx from 'clsx';
import { useGSAP } from '@gsap/react';

interface BoxProps {
  title?: string;
  text?: string;
  image: {
    src?: string;
    alt?: string;
    width?: number;
    height?: number;
    href?: string;
  };
  avatar: {
    image: {
      src: string;
      alt: string;
      width: number;
      height: number;
      href: string;
    };
    name?: string;
    position?: string;
    company?: string;
    quote?: string;
  };
}

interface ReportingSectionProps {
  data: BoxProps[];
  title?: string;
  subtitle?: string;
}

const animationBox = (ref: React.RefObject<HTMLDivElement | null>) => {
  if (!ref.current) return;
  const pinSt = gsap.timeline({
    defaults: {
      ease: 'none',
    },
    scrollTrigger: {
      trigger: ref.current,
      // start animation at --navbarHeight had to decrease this to avoid white flash from overscroll
      start: 'top top+=40',
      end: 'bottom center',
      toggleActions: 'play none none reverse',
      pin: true,
      scrub: true,
    },
  });

  const boxes = ref.current.querySelectorAll('.box-item');
  boxes?.forEach((box, index) => {
    const tl = gsap.timeline();

    tl.from(box, {
      opacity: 0,
      y: 140,
    });

    // hide all but the last box
    if (index !== boxes.length - 1) {
      tl.to(box, {
        opacity: 0,
        delay: 0.25,
        y: -50,
      });
    }

    pinSt.add(tl);
  });
};

const animationMobileBox = (ref: React.RefObject<HTMLDivElement | null>) => {
  const { current } = ref;
  if (!current) return;
  const boxes = current.querySelectorAll('.box-item');
  boxes?.forEach(box => {
    gsap
      .timeline({
        scrollTrigger: {
          trigger: box,
          start: 'top center',
          end: 'bottom center',
          toggleActions: 'play none none reverse',
          markers: false,
        },
      })
      .from([box.querySelector('figure'), box.querySelector('h4'), box.querySelector('h5')], {
        opacity: 0,
        duration: 1,
        x: -50,
        stagger: 0.2,
        ease: 'expo.out',
      });
  });
};

const renderBoxSection = ({ title, text, image, avatar }: BoxProps) => (
  <div className="flex flex-col-reverse gap-6 p-0 lg:flex-row lg:gap-0">
    <div className="w-full pr-0 text-white md:w-full md:pr-0 lg:w-1/2 lg:pr-14">
      <h4 className="text-h6 mb-4">{title}</h4>
      <p className="text-body mb-10 text-gray-4">{text}</p>
      <div className="hidden items-center gap-4 md:flex">
        <div className="w-24 rounded-full bg-white p-2">
          <Image
            src={`${avatar.image.src}`}
            alt={`${avatar.image.alt}`}
            width={60}
            height={60}
            className="object-cover"
          />
        </div>
        <div>
          <p className="md:text-body-md-quote text-body-lg-quote mb-4 font-bold italic">{avatar.quote}</p>
          <p className="text-body-sm">{avatar.name}</p>
        </div>
      </div>
    </div>
    <div className="w-full md:w-full lg:w-1/2">
      <figure>
        <Image src={`${image.src}`} alt={`${image.alt}`} width={739} height={498} />
      </figure>
    </div>
  </div>
);

export const ReportingSection: React.FC<ReportingSectionProps> = ({ title, subtitle, data }) => {
  const ref = React.useRef<HTMLDivElement>(null);
  const animationData = useLottieLoader('https://lottie.host/efac8bab-0e90-4540-aed8-be75e44a4163/Gy0nP2af72.json');
  useGSAP(
    () => {
      gsap.registerPlugin(ScrollTrigger);

      if (window.innerWidth > SM_BREAKPOINT) {
        animationBox(ref);
      } else {
        animationMobileBox(ref);
      }
    },
    { scope: ref },
  );

  return (
    <div className="overflow-hidden bg-gray-1 py-10 md:py-20" ref={ref}>
      <div className="container relative pb-20 md:pb-40">
        <div className="relative z-10 text-center">
          <Title title={title} subtitle={subtitle} className="fade-up lg:mb-5 xl:max-w-5xl" color="white" />
          <Button href="/develocity/observe/" variant="primary" className="ani-up">
            Learn More
          </Button>
        </div>
        <div className="relative">
          {data.map((item, index) => (
            <div
              key={`${item.title}-${index}`}
              className={clsx({
                'relative left-0 top-0 md:absolute': index === 1,
                relative: index !== 1,
                'box-item flex px-10 py-0 text-left text-white md:py-10': true,
              })}
            >
              {renderBoxSection(item)}
            </div>
          ))}
        </div>
        <Lottie
          animationData={animationData}
          autoplay={true}
          loop={true}
          className="absolute -bottom-32 left-0 m-0 h-60 w-60 md:-bottom-2/12"
        />
      </div>
    </div>
  );
};
